import { JSX, useCallback, useEffect, useRef, useState } from "react";
import Container, { Column, Row } from "@/components/container/Container";
import { ActivityBlockGroupBy, getActivityCardComponent, mapActivitiesToViewModels, TripData, X4bActivitySection } from "../engine/ConciergeCardViewModel";
import ConciergeHeader from "./ConciergeHeader";
import ConciergeCardInfo from "./ConciergeCardInfo";
import { useParams } from "react-router-dom";
import { TextHeadlineLarge, TextLabelLarge2, TextTitleLarge2, TextTitleMedium2 } from "@/components/text/Texts";
import { CardContext } from "../context/CardContext";
import ConciergeSidebar from "./ConciergeSidebar";

import { Button } from "primereact/button";
import { MdAddCircle } from "react-icons/md";
import { Menu } from "primereact/menu";
import CreateNewRideMenu from "./CreateNewRideMenu";
import { axiosInstance } from "@/services/axios.service";
import { GenericResponse, showTypedModal2 } from "@/common/common_utils";
import { ENDPOINTS } from "@/constants/endpoints";
import { useQuery } from "@tanstack/react-query";
import { OrderChangedDto, useOrderStatusBus } from "@/services/signalr.service";
import { showSuccess } from "@/modules/notification/engine/notification.library";
import moment from "moment";
import AutoCompleteSearch from "@/components/search-bar/AutocompleteSearchBar";
import { ActivityHelper, X4bActivityDto } from "@/modules/vehicle/engine/X4bActivityDto";
import { X4bUserRoleEnum } from "@/constants/roles";
import { useAuthenticationStore } from "@/store";
import ConciergeRideDetailsDialog from "@/modules/concierge/views/ConciergeRideDetailsDialog";
import { useIsTablet } from "@/common/useIsTablet";

interface ActivityBlock {
	//ActivitySection: X4bActivitySection;
	//Date: string;
	BlockTitle: string;
	Activities: X4bActivityDto[];
}

interface GetActivityBlockListRequest {
	ActivitySection: X4bActivitySection;
	GroupBy: ActivityBlockGroupBy;
	NamePhone?: string
}
interface GetActivityBlockListResponse { Blocks: ActivityBlock[]; TotalRecords: number }

const ConciergeScreen = () => {
	const menuRef = useRef<Menu>(null);
	const [selectedCard, setSelectedCard] = useState<TripData | null>(null);
	const statusParam = parseInt(useParams<{ id: string }>()?.id ?? '0') as X4bActivitySection;
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);
	const isTablet = useIsTablet();

	const [search, setSearch] = useState<string>('');
	const [namePhoneFilter, setNamePhoneFilter] = useState<string>('');

	const { data: activityBlocks, isFetching, refetch } = useQuery({
		queryKey: ['Concierge.trips', statusParam, namePhoneFilter],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<GetActivityBlockListResponse>>(ENDPOINTS['/api/X4bActivity/GetActivityBlockList'], {
				ActivitySection: statusParam,
				NamePhone: namePhoneFilter,
				GroupBy: [X4bActivitySection.Completed, X4bActivitySection.Canceled, X4bActivitySection.Scheduled, X4bActivitySection.PastActivity].includes(statusParam) ? ActivityBlockGroupBy.Date : ActivityBlockGroupBy.Status
			} as GetActivityBlockListRequest);
			return result.data.Data;
		},
		initialData: { Blocks: [], TotalRecords: 0 } as GetActivityBlockListResponse
	});

	useEffect(() => {
		setSearch('');
		setNamePhoneFilter('');
	}, [statusParam]);

	const handleSearchSelect = (activity: X4bActivityDto) => {
		setNamePhoneFilter(activity.HeadPassenger.FirstName! + ' ' + activity.HeadPassenger.LastName!);
	}

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setNamePhoneFilter(value);
	};

	const handleSelectedCardChange = useCallback((card: TripData | null) => {
		setSelectedCard(card);

		if (card !== null && isTablet) {
			void showTypedModal2(ConciergeRideDetailsDialog, {
				selectedCard: card,
				onClose: () => setSelectedCard(null),
			});
		}
	}, [isTablet]);

	useOrderStatusBus(async (e: OrderChangedDto) => {
		// Not only status but also times can change.
		console.log(`Order ${e.bookingId} realtime data changed.`);
		//showSuccess('Order status changed to ' + e.status);
		refetch();
		// if (e.status == X4bActivityStatus.RiderAssigned) await onStartRouting();
	});

	const getCards = (trips: X4bActivityDto[]) => {
		const cards: JSX.Element[] = [];
		mapActivitiesToViewModels(trips).forEach(trip => {
			const CardComponent = getActivityCardComponent(trip);
			const extraProps = ActivityHelper.getActivityCardExtraProps(trip.act, refetch) || {};


			cards.push(
				<CardComponent
					key={`ConciergeCard-${trip.id}`}
					tripInfo={trip}
					{...extraProps}
				/>
			);
		});

		return cards.reverse();
	}

	let renderedBlocks: JSX.Element | undefined;
	if ([X4bActivitySection.TodaysActivity].includes(statusParam)) {
		renderedBlocks = (
			<Column className="overflow-y-auto no-scrollbar gap-8 w-[412px] flex-none tablet:w-auto tablet:flex-1 py-6">
				{activityBlocks.Blocks.map((block, index) => (
					<Column className="gap-4" key={`${block.BlockTitle}`}>
						<TextHeadlineLarge
							text={block.BlockTitle || ''}
							className="text-black"
						/>
						{getCards(block.Activities)}
					</Column>
				))}
			</Column>
		);
	} else if ([X4bActivitySection.NeedsAttention, X4bActivitySection.Scheduled, X4bActivitySection.Canceled,
	X4bActivitySection.PastActivity, X4bActivitySection.Completed].includes(statusParam)) {
		const title = ActivityHelper.getSectionName(statusParam) || '';

		const suggestions = activityBlocks.Blocks.map(block => {
			block.Activities.map((activity: any) => {
				activity.PassengerPhone = activity.HeadPassenger.Phone;
				activity.PassengerName = activity.HeadPassenger.FirstName + ' ' + activity.HeadPassenger.LastName;
				return activity;
			});
			return block;
		}).flatMap((activityBlock) => activityBlock.Activities);

		renderedBlocks = (
			<Column className="overflow-y-auto no-scrollbar gap-4 w-[412px] flex-none tablet:w-auto tablet:flex-1 py-6">
				<TextHeadlineLarge text={title} className="text-black" />

				<AutoCompleteSearch<any>
					suggestions={suggestions}
					placeholder="Search Passenger, Phone no."
					onSelect={handleSearchSelect}
					onChange={handleSearchChange}
					filterMethod={() => true}
					label="PassengerName"
					description="PassengerPhone"
					value={search}
					className="w-full"
				/>

				{activityBlocks.Blocks.map(block => (
					<>
						<TextTitleLarge2
							text={block.BlockTitle}
							className="text-black"
						/>
						{getCards(block.Activities)}
					</>
				))}
			</Column>
		);
	} else if ([X4bActivitySection.AwaitingPickup, X4bActivitySection.Requesting, X4bActivitySection.InProgress].includes(statusParam)) {
		const title = ActivityHelper.getSectionName(statusParam) || '';
		const activities = activityBlocks.Blocks
			.flatMap(block => block.Activities)
			.reverse();

		renderedBlocks = (
			<Column className="overflow-y-auto no-scrollbar gap-4 w-[412px] flex-none tablet:w-auto tablet:flex-1 py-6">
				<TextHeadlineLarge text={title} className="text-black" />
				{getCards(activities)}
			</Column>
		);
	}

	return (
		<>
			<CreateNewRideMenu ref={menuRef} className="w-[280px]" />

			<Column className="w-full h-screen">
				<ConciergeHeader />
				<Row className="w-full h-[calc(100%-80px)]"> {/* 80px - header */}
					<CardContext.Provider value={{ selectedCard, setSelectedCard: handleSelectedCardChange }}>
						{/* Sidebar */}
						<Container
							className="flex-none w-[260px] tablet:w-[224px]"
							children={(<ConciergeSidebar />)}
						/>
						{(activityBlocks.TotalRecords === 0 && !isFetching) ? (
							<Container className="w-full flex justify-center items-center">
								<Column>
									<img className="mb-3 px-[8.5px]" src="/images/no_active_rides.png" alt="no active rides" />
									<TextLabelLarge2 className="mb-6 text-black" text="There are no active rides right now" />
									{isManagerOrUser &&
										<Button
											id='concierge-create-new-ride'
											label="Create new"
											onClick={(e) => menuRef.current?.toggle(e)}
											icon={<MdAddCircle className="text-white" size={18} />}
											pt={{
												root: { className: 'gap-2 justify-center h-10 rounded-[100px]' },
												label: { className: 'grow-0 text-label-large2 text-white' },
											}}
										/>}
								</Column>
							</Container>
						) : (
							<>
								{/* Card List */}
								{renderedBlocks}

								{/* Card Info */}
								{selectedCard === null ? (
									<Column className="gap-3 w-full justify-center items-center tablet:hidden">
										<img src="/images/concierge/circles.png" alt="circles" />
										<TextTitleMedium2 text="Select something first" className="text-black" />
									</Column>
								) : (
									<Container
										className="overflow-y-auto no-scrollbar p-6 flex-1 tablet:hidden"
										children={(<ConciergeCardInfo />)}
									/>
								)}
							</>
						)}
					</CardContext.Provider>
				</Row>
			</Column>
		</>
	);
};

export default ConciergeScreen;


